import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Search, Bookmark, BookmarkCheck, Download, FileText, Video } from "lucide-react";
import { toast } from "sonner";
import { api, useApi, fmtDate, errMsg, downloadFile, imgSrc } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Spinner, ErrorBox, Empty, PageHead, AccessBadge, JoinCta } from "../components/common";

const TYPES = ["glaze recipe guide", "firing schedule", "studio business guide", "workshop recording", "market report"];

function BookmarkBtn({ item, onChange }) {
  const { user } = useAuth();
  if (!user) return null;
  const toggle = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post(`/library/${item.id}/bookmark`);
      onChange?.(data.bookmarked);
      toast.success(data.bookmarked ? "Saved to your library" : "Removed from saved items");
    } catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <button onClick={toggle} className="rounded-full p-2 text-ink hover:bg-sand" aria-label={item.bookmarked ? "Remove bookmark" : "Bookmark this item"} data-testid={`bookmark-btn-${item.slug}`}>
      {item.bookmarked ? <BookmarkCheck className="h-5 w-5 text-ochre-dark" /> : <Bookmark className="h-5 w-5" />}
    </button>
  );
}

export default function Library() {
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [tag, setTag] = useState("");
  const [sort, setSort] = useState("newest");
  const [dq, setDq] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDq(q), 300);
    return () => clearTimeout(t);
  }, [q]);
  const { data, loading, error, reload, setData } = useApi(`/library?q=${encodeURIComponent(dq)}&type=${encodeURIComponent(type)}&tag=${encodeURIComponent(tag)}&sort=${sort}`);
  const mark = (id, v) => setData({ ...data, items: data.items.map((i) => (i.id === id ? { ...i, bookmarked: v } : i)) });
  return (
    <div>
      <PageHead eyebrow="Resource library" title="Recipes, schedules & studio know-how" intro="Free guides are open to everyone. Members-only items show a short preview — join to read the rest." />
      <section className="kh-container">
        <div className="mb-8 grid gap-3 rounded-2xl border border-line bg-white p-4 md:grid-cols-4">
          <div className="relative md:col-span-1">
            <label htmlFor="lib-search" className="sr-only">Search library</label>
            <Search className="absolute left-3 top-3 h-4 w-4 text-[#6b6f78]" aria-hidden="true" />
            <input id="lib-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search titles & topics" className="kh-input pl-9" data-testid="library-search-input" />
          </div>
          <label className="sr-only" htmlFor="lib-type">Type</label>
          <select id="lib-type" value={type} onChange={(e) => setType(e.target.value)} className="kh-input" data-testid="library-type-filter">
            <option value="">All types</option>{TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <label className="sr-only" htmlFor="lib-tag">Topic</label>
          <select id="lib-tag" value={tag} onChange={(e) => setTag(e.target.value)} className="kh-input" data-testid="library-topic-filter">
            <option value="">All topics</option>{data?.tags?.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <label className="sr-only" htmlFor="lib-sort">Sort</label>
          <select id="lib-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="kh-input" data-testid="library-sort-select">
            <option value="newest">Newest first</option><option value="oldest">Oldest first</option>
          </select>
        </div>
        {loading && !data ? <Spinner /> : error ? <ErrorBox message={error} onRetry={reload} /> : !data.items.length ? (
          <Empty title="No resources match" text="Try a different search term or clear the filters." action={<button className="btn-ghost" onClick={() => { setQ(""); setType(""); setTag(""); }}>Clear filters</button>} />
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {data.items.map((i) => (
              <Link key={i.id} to={`/library/${i.slug}`} className="kh-card group flex flex-col overflow-hidden" data-testid={`library-card-${i.slug}`}>
                <div className="relative"><img src={imgSrc(i.cover)} alt="" className="h-40 w-full object-cover" /><div className="absolute left-3 top-3"><AccessBadge level={i.access_level} locked={i.locked} /></div></div>
                <div className="flex flex-1 flex-col p-5">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-celadon-dark">{i.type}</p>
                    <BookmarkBtn item={i} onChange={(v) => mark(i.id, v)} />
                  </div>
                  <h2 className="text-xl leading-snug">{i.title}</h2>
                  <p className="mt-2 flex-1 text-sm text-[#4a4f59]">{i.summary}</p>
                  <p className="mt-4 text-xs text-[#5a5f69]">{i.author} · {fmtDate(i.date)}</p>
                  <div className="mt-2 flex flex-wrap gap-1">{i.tags.map((t) => <span key={t} className="chip">{t}</span>)}</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function PdfViewer({ item }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let u;
    api.get(`/library/${item.id}/file`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch((e) => setErr(errMsg(e)));
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [item.id]);
  if (err) return <ErrorBox message={err} />;
  if (!url) return <Spinner label="Loading PDF…" />;
  return <iframe title={`${item.title} PDF`} src={url} className="h-[70vh] w-full rounded-2xl border border-line bg-white" data-testid="pdf-viewer" />;
}

export function LibraryItem() {
  const { slug } = useParams();
  const { data: i, loading, error, setData } = useApi(`/library/${slug}`);
  if (loading) return <Spinner />;
  if (error) return <div className="kh-container py-16"><ErrorBox message={error} /></div>;
  return (
    <article className="kh-container max-w-4xl pt-12">
      <Link to="/library" className="text-sm text-ink underline decoration-ochre" data-testid="back-to-library">← Back to library</Link>
      <div className="mt-6 flex flex-wrap items-center gap-2"><AccessBadge level={i.access_level} locked={i.locked} /><span className="chip">{i.type}</span>{i.tags.map((t) => <span key={t} className="chip">{t}</span>)}</div>
      <div className="mt-4 flex items-start justify-between gap-4">
        <h1 className="text-4xl sm:text-5xl">{i.title}</h1>
        {!i.locked && <BookmarkBtn item={{ ...i, slug: "detail" }} onChange={(v) => setData({ ...i, bookmarked: v })} />}
      </div>
      <p className="mt-3 text-sm text-[#5a5f69]">{i.author} · {fmtDate(i.date)}</p>
      <p className="mt-6 text-lg text-[#3d424c]">{i.summary}</p>
      {i.locked ? (
        <div className="mt-8" data-testid="library-locked-view">
          <div className="relative">
            <p className="text-base leading-relaxed" data-testid="library-excerpt">{i.excerpt}…</p>
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-paper to-transparent" />
          </div>
          <p className="my-6 flex items-center gap-2 text-sm text-[#4a4f59]">
            {i.has_file && <><FileText className="h-4 w-4" aria-hidden="true" /> Includes a downloadable PDF</>}
            {i.has_video && <><Video className="h-4 w-4" aria-hidden="true" /> Includes a workshop recording</>}
          </p>
          <JoinCta text="Join to read the full guide" />
        </div>
      ) : (
        <div className="mt-8 space-y-8" data-testid="library-full-view">
          {i.video_url && <video controls src={i.video_url} className="w-full rounded-2xl bg-black" data-testid="library-video"><track kind="captions" /></video>}
          {i.file_id && (
            <div>
              <div className="mb-3 flex items-center justify-between"><h2 className="text-2xl">Document</h2>
                <button className="btn-primary btn-sm" onClick={() => downloadFile(`/library/${i.id}/file?download=1`, `${i.slug}.pdf`).catch((e) => toast.error(errMsg(e)))} data-testid="library-download-btn"><Download className="h-4 w-4" aria-hidden="true" />Download PDF</button>
              </div>
              <PdfViewer item={i} />
            </div>
          )}
          <div className="prose-kh" dangerouslySetInnerHTML={{ __html: i.body_html }} />
        </div>
      )}
    </article>
  );
}
